import { Resources } from 'constants/resources';
import { SECURITY_ISSUES_ROWS_BG } from 'constants/securityIssues';

import ListAltIcon from '@mui/icons-material/ListAlt';
import Percent from '@mui/icons-material/Percent';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { ProvidersDeleteDialog } from 'pages/Providers/components/ProvidersDeleteDialog/ProvidersDeleteDialog';
import { CancelButton } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { IconLabel } from 'shared/mui/Labels/IconLabel/IconLabel';
import {
  BooleanField,
  FunctionField,
  TextField,
  TooltipTextField,
} from 'shared/react-admin/Fields';
import { SecurityIssuesField } from 'shared/react-admin/Fields/SecurityIssuesField/SecurityIssuesField';
import { TooltipTextLinkField } from 'shared/react-admin/Fields/TooltipTextLinkField/TooltipTextLinkField';
import { Permission } from 'types/embedded/permission';
import { Provider } from 'types/embedded/provider';

import { DatagridStyled } from './ProvidersListDatagrid.styled';
import { ProvidersListDatagridCommissionsField } from './ProvidersListDatagridCommissionsField';
import { ProvidersListDatagridWhiteIpsField } from './ProvidersListDatagridWhiteIpsField';

export const ProvidersListDatagrid: React.FC = () => {
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();

  const {
    record: recordDelete,
    handleOpenRecordModal: handleOpenRecordModalDelete,
    openRecordModal: openRecordModalDelete,
    handleCloseRecordModal: handleCloseRecordModalDelete,
  } = useRecordModal<Provider>();

  return (
    <>
      <DatagridStyled
        bulkActionButtons={false}
        rowSx={({ securityIssues }: Provider) => ({
          background: SECURITY_ISSUES_ROWS_BG[securityIssues[0]],
        })}
      >
        <TextField label="ID" sortBy="id" source="originId" />
        <TooltipTextLinkField label="Название" resource="providers" source="name" type="show" />
        <TooltipTextField label="Алиас" source="alias" />
        <BooleanField label="Активен" source="active" />
        {hasResourceAccess(Resources.Commissions) && (
          <FunctionField
            cellClassName="column-commissions"
            label={<IconLabel icon={Percent} label="Комиссии" />}
            render={() => <ProvidersListDatagridCommissionsField />}
          />
        )}
        {hasResourceAccess('provider_white_ip_addresses') && (
          <FunctionField
            cellClassName="column-white_ip_addresses"
            label={<IconLabel icon={ListAltIcon} label="Белые IP" />}
            render={() => <ProvidersListDatagridWhiteIpsField />}
          />
        )}
        <SecurityIssuesField label="Уведомления безопасности" source="securityIssues" />
        <TooltipTextField label="Комментарий" source="comment" />
        <ContextMenu
          render={(record: Provider) => [
            hasResourceAccess(Resources.UserActions) && (
              <ContextMenuEntityUserActionsButton
                entityId={record?.originId}
                entityName="provider"
                key="user-action-link"
              />
            ),
            <ContextMenuShowButton key="show-button" resource="providers" />,
            hasResourcePermissions(Resources.Providers, [Permission.Full, Permission.Update]) && (
              <ContextMenuEditButton
                key="edit-button"
                linkProps={{
                  resource: Resources.Providers,
                }}
              />
            ),
            hasResourcePermissions(Resources.Providers, [Permission.Full, Permission.Delete]) && (
              <ContextMenuDeleteButton
                key="delete-button"
                onClick={() => handleOpenRecordModalDelete(record)}
              />
            ),
          ]}
        />
      </DatagridStyled>
      {openRecordModalDelete && (
        <ProvidersDeleteDialog
          onClose={handleCloseRecordModalDelete}
          open={openRecordModalDelete}
          record={recordDelete}
        >
          <CancelButton
            key="history-back-button"
            label="Вернуться к списку провайдеров"
            onClick={handleCloseRecordModalDelete}
            variant="outlined"
          />
        </ProvidersDeleteDialog>
      )}
    </>
  );
};
